import React, { useMemo, useState, useEffect, useCallback } from "react";
import Box from "components/common/Box";
import Text from "components/common/Text";
import Chart from "components/common/Chart";
import Grid from "components/common/Grid";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { usePhcTheme } from "../PhcThemeContext";

const BENCHMARKS = [
  {
    key: "portfolio",
    label: "PORTFOLIO",
    mobileLabel: "PORTFOLIO",
    color: "#45AC70",
    mobileColor: "#0066FF",
    activeBg: "rgba(69,172,112,0.2)",
    alwaysOn: true,
    dataKey: "portfolio",
  },
  {
    key: "nifty50",
    label: "NIFTY 50",
    mobileLabel: "NIFTY 50",
    color: "#007EF0",
    mobileColor: "#AEBCBC",
    activeBg: "rgba(0,126,240,0.2)",
    alwaysOn: true,
    dataKey: "nifty50",
  },
  {
    key: "nifty500",
    label: "NIFTY 500",
    mobileLabel: "NIFTY 500",
    color: "#F6A823",
    mobileColor: "#F6A823",
    activeBg: "rgba(246,168,35,0.2)",
    dataKey: "nifty500",
  },
  {
    key: "niftyMidcap",
    label: "NIFTY MIDCAP",
    mobileLabel: "MIDCAP",
    color: "#A846B9",
    mobileColor: "#A846B9",
    activeBg: "rgba(168,70,185,0.2)",
    dataKey: "niftyMidcap",
  },
  {
    key: "niftySmallcap",
    label: "NIFTY SMALLCAP",
    mobileLabel: "SMALLCAP",
    color: "#FF7043",
    mobileColor: "#FF7043",
    activeBg: "rgba(255,112,67,0.2)",
    dataKey: "niftySmallcap",
  },
];

const FOOTER_FALLBACK = {
  portfolio: "—",
  nifty50: "—",
  nifty500: "—",
  niftyMidcap: "—",
  niftySmallcap: "—",
};

/** Mobile metric card colors — readable in both light and dark */
const MOBILE_METRIC_STYLE = {
  portfolio: {
    valueColor: "#45AC70",
    barColor: "#45AC70",
    barTrack: "rgba(69,172,112,0.15)",
    barGlow: "rgba(69,172,112,0.45)",
    borderLeft: "#45AC70",
  },
  nifty50: {
    valueColor: "#007EF0",
    barColor: "#007EF0",
    barTrack: "rgba(0,126,240,0.15)",
    barGlow: "rgba(0,126,240,0.45)",
    borderLeft: "#007EF0",
  },
  nifty500: {
    valueColor: "#F6A823",
    barColor: "#F6A823",
    barTrack: "rgba(246,168,35,0.15)",
    barGlow: "rgba(246,168,35,0.45)",
    borderLeft: "#F6A823",
  },
  niftyMidcap: {
    valueColor: "#A846B9",
    barColor: "#A846B9",
    barTrack: "rgba(168,70,185,0.15)",
    barGlow: "rgba(168,70,185,0.45)",
    borderLeft: "#A846B9",
  },
};

const MONTH_INDEX = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

const processSeries = (data) =>
  (data || [])
    .map((item, index) => {
      const rawDate = item?.date;
      let timestamp = NaN;

      // Month labels like "Mar 2026" must be UTC so Highcharts hover/axis
      // stay on that month (local midnight in IST becomes previous month in UTC).
      if (typeof rawDate === "string") {
        const monthMatch = rawDate.trim().match(/^([A-Za-z]{3})\s+(\d{4})$/);
        if (monthMatch) {
          const month = MONTH_INDEX[monthMatch[1]];
          const year = Number(monthMatch[2]);
          if (month != null && !Number.isNaN(year)) {
            timestamp = Date.UTC(year, month, 1);
          }
        }
      }

      if (Number.isNaN(timestamp)) {
        timestamp = new Date(rawDate).getTime();
      }

      // Last-resort sequential axis so chart still renders if date is unparsable
      if (Number.isNaN(timestamp)) {
        timestamp = index;
      }

      const value = Number(item.value);
      if (Number.isNaN(value)) return null;
      return [timestamp, value];
    })
    .filter(Boolean);

const Y_AXIS_STEP = 10;

/** Round the plotted range to steps of 10, with a step of padding at an exact edge. */
const yAxisBounds = (series) => {
  const values = series
    .flatMap((item) => (item.data || []).map((point) => Number(point[1])))
    .filter((value) => !Number.isNaN(value));
  if (!values.length) return {};

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  let min = Math.floor(dataMin / Y_AXIS_STEP) * Y_AXIS_STEP;
  let max = Math.ceil(dataMax / Y_AXIS_STEP) * Y_AXIS_STEP;
  if (min === dataMin) min -= Y_AXIS_STEP;
  if (max === dataMax) max += Y_AXIS_STEP;
  if (min === max) max += Y_AXIS_STEP;

  return { min, max, tickInterval: Y_AXIS_STEP };
};

/** Monday 00:00 UTC of the week containing this timestamp. */
const weekStartUtc = (ts) => {
  const date = new Date(ts);
  const day = date.getUTCDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() + mondayOffset,
  );
};

const calcReturnPct = (data) => {
  if (!Array.isArray(data) || data.length < 2) {
    if (data?.length === 1 && data[0]?.value != null) {
      const v = Number(data[0].value);
      if (!Number.isNaN(v)) return v;
    }
    return null;
  }
  const first = Number(data[0].value);
  const last = Number(data[data.length - 1].value);
  if (Number.isNaN(first) || Number.isNaN(last)) return null;
  if (Math.abs(first) < 1) return last;
  return last - first;
};

const formatReturn = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const num = Number(value);
  const sign = num >= 0 ? "+" : "";
  return `${sign}${num.toFixed(1)}%`;
};

const parseReturnNum = (display) => {
  if (display == null || display === "—") return 0;
  return Math.abs(Number(String(display).replace(/[+%]/g, "")) || 0);
};

const resolveReturn = (apiValue, seriesData, fallback) => {
  if (apiValue != null) {
    if (typeof apiValue === "string" && apiValue.includes("%")) return apiValue;
    return formatReturn(apiValue);
  }
  const computed = calcReturnPct(seriesData);
  if (computed != null) return formatReturn(computed);
  return fallback ?? "—";
};

const getSeriesFill = (color, isMobile, isPortfolio) => {
  if (isMobile) {
    return isPortfolio
      ? {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, `${color}55`],
            [1, `${color}05`],
          ],
        }
      : "transparent";
  }

  return {
    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
    stops: [
      [0, isPortfolio ? `${color}40` : `${color}1F`],
      [1, `${color}05`],
    ],
  };
};

const DesktopChip = ({ benchmark, active, onClick }) => {
  const { theme } = usePhcTheme();
  return (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      px: "12px",
      py: "10px",
      borderRadius: "12px",
      cursor: "pointer",
      flexShrink: 0,
      whiteSpace: "nowrap",
      ...(active
        ? { bgcolor: benchmark.activeBg, border: "none" }
        : {
            bgcolor: "transparent",
            border: `1px solid ${theme.sectionBorder}`,
            opacity: 0.6,
          }),
    }}
  >
    <Text
      sx={{
        fontWeight: 700,
        fontSize: 16,
        color: active ? benchmark.color : theme.textPrimary,
        whiteSpace: "nowrap",
        lineHeight: "normal",
      }}
    >
      {benchmark.label}
    </Text>
  </Box>
  );
};

/** Mobile pill chip — */
const MobileChip = ({ benchmark, active, onClick, theme }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      px: "13px",
      py: "7px",
      borderRadius: "9999px",
      cursor: "pointer",
      flexShrink: 0,
      whiteSpace: "nowrap",
      bgcolor: active ? theme.mobileChipActiveBg : theme.mobileChipIdleBg,
      border: active
        ? `1px solid ${theme.mobileChipActiveBorder}`
        : `1px solid ${theme.mobileChipIdleBorder}`,
    }}
  >
    <Text
      sx={{
        fontWeight: 400,
        fontSize: 11,
        color: active ? theme.mobileChipAccent : theme.mobileLabelColor,
        lineHeight: "16.5px",
        textAlign: "center",
        whiteSpace: "nowrap",
      }}
    >
      {benchmark.mobileLabel || benchmark.label}
    </Text>
  </Box>
);

/** Mobile return metric card — */
const MobileMetricCard = ({ label, value, style, fillPct, theme }) => (
  <Box
    sx={{
      width: "100%",
      borderRadius: "12px",
      borderTop: `1px solid ${theme.mobileMetricBorder}`,
      borderRight: `1px solid ${theme.mobileMetricBorder}`,
      borderBottom: `1px solid ${theme.mobileMetricBorder}`,
      borderLeft: `4px solid ${style.borderLeft}`,
      bgcolor: theme.mobileCardBg,
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      pt: "16px",
      pb: "17px",
      pl: "20px",
      pr: "17px",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minHeight: 102.5,
      boxSizing: "border-box",
    }}
  >
    <Text
      sx={{
        fontSize: 11,
        color: theme.mobileLabelColor,
        textTransform: "uppercase",
        lineHeight: "16.5px",
      }}
    >
      {label}
    </Text>
    <Text
      sx={{
        fontWeight: 500,
        fontSize: 28,
        color: style.valueColor,
        lineHeight: "32px",
      }}
    >
      {value}
    </Text>
    <Box sx={{ pt: "8px", width: "100%" }}>
      <Box
        sx={{
          height: 4,
          width: "100%",
          borderRadius: "9999px",
          bgcolor: style.barTrack,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${Math.max(8, Math.min(100, fillPct))}%`,
            bgcolor: style.barColor,
            boxShadow: `0px 0px 8px 0px ${style.barGlow}`,
          }}
        />
      </Box>
    </Box>
  </Box>
);

/**
 * Performance Chart — desktop  / mobile 
 */
const PerformanceChart = ({
  portfolioData = [],
  benchmarkData = [],
  extraBenchmarks = {},
  footerReturns = {},
}) => {
  const { theme, mode } = usePhcTheme();
  const isMobile = useMediaQuery("(max-width:899px)");
  const isLight = mode === "light";
  const [isLoading, setIsLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [activeKeys, setActiveKeys] = useState(["portfolio", "nifty50"]);

  const dataByKey = useMemo(
    () => ({
      portfolio: portfolioData,
      nifty50: benchmarkData,
      nifty500: extraBenchmarks.nifty500 || extraBenchmarks.nifty500Data,
      niftyMidcap:
        extraBenchmarks.niftyMidcap || extraBenchmarks.niftyMidcapData,
      niftySmallcap:
        extraBenchmarks.niftySmallcap || extraBenchmarks.niftySmallcapData,
    }),
    [portfolioData, benchmarkData, extraBenchmarks],
  );

  const hasChartData =
    processSeries(portfolioData).length > 0 ||
    processSeries(benchmarkData).length > 0;

  const allBenchmarkKeys = useMemo(
    () => BENCHMARKS.map((benchmark) => benchmark.key),
    [],
  );

  useEffect(() => {
    if (portfolioData?.length || benchmarkData?.length) {
      setIsLoading(false);
    }
  }, [portfolioData, benchmarkData]);

  const toggleBenchmark = (key) => {
    const bench = BENCHMARKS.find((b) => b.key === key);
    // Portfolio always on; Nifty 50 always on desktop only
    if (bench?.key === "portfolio") return;
    if (!isMobile && bench?.alwaysOn) return;
    if (!isMobile) setShowAll(false);
    setActiveKeys((prev) => {
      if (prev.includes(key)) {
        // Keep at least portfolio selected
        const next = prev.filter((k) => k !== key);
        return next.length ? next : ["portfolio"];
      }
      return [...prev, key];
    });
  };

  const handleToggleShowAll = useCallback(() => {
    if (isMobile) return;

    setShowAll((prev) => {
      const next = !prev;
      setActiveKeys(next ? allBenchmarkKeys : ["portfolio", "nifty50"]);
      return next;
    });
  }, [allBenchmarkKeys, isMobile]);

  const chartOptions = useMemo(() => {
    const series = BENCHMARKS.filter((b) => activeKeys.includes(b.key))
      .map((b) => {
        const points = processSeries(dataByKey[b.dataKey]);
        if (!points.length) return null;
        const color =
          isMobile && !isLight ? b.mobileColor || b.color : b.color;
        const isPortfolio = b.key === "portfolio";
        return {
          name: b.label,
          type: "areaspline",
          data: points,
          color,
          dashStyle: isMobile && !isPortfolio ? "Dash" : "Solid",
          fillColor: getSeriesFill(color, isMobile, isPortfolio),
          fillOpacity: isMobile ? (isPortfolio ? 0.35 : 0) : isPortfolio ? 0.28 : 0.14,
          lineWidth: isMobile ? (isPortfolio ? 2.5 : 1.5) : isPortfolio ? 2.2 : 1.8,
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 5,
                fillColor: color,
                lineWidth: 0,
              },
            },
          },
          states: { hover: { lineWidth: 3 } },
        };
      })
      .filter(Boolean);

    const weekTicks = [
      ...new Set(
        series
          .flatMap((s) => (s.data || []).map((point) => point[0]))
          .map(weekStartUtc),
      ),
    ].sort((a, b) => a - b);
    const maxLabels = isMobile ? 6 : 12;
    const labelStep =
      weekTicks.length > maxLabels
        ? Math.ceil(weekTicks.length / maxLabels)
        : 1;
    const tickPositions =
      labelStep > 1
        ? weekTicks.filter((_, index) => index % labelStep === 0)
        : [...weekTicks];
    const lastTick = weekTicks[weekTicks.length - 1];
    const prevTick = tickPositions[tickPositions.length - 1];
    const minGap = (labelStep * 7 * 24 * 3600 * 1000) / 2;
    if (lastTick != null && prevTick != null && lastTick !== prevTick) {
      if (lastTick - prevTick < minGap) {
        tickPositions[tickPositions.length - 1] = lastTick;
      } else {
        tickPositions.push(lastTick);
      }
    }

    return {
      chart: {
        type: "areaspline",
        height: isMobile ? 248 : 321,
        backgroundColor: "transparent",
        spacing: isMobile ? [12, 8, 28, 8] : [30, 18, 32, 18],
        style: { fontFamily: "inherit" },
      },
      title: { text: null },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        type: "datetime",
        lineWidth: isMobile ? 1 : 0,
        lineColor: theme.chartAxisColor,
        tickWidth: 0,
        gridLineWidth: 0,
        tickPositions: tickPositions.length ? tickPositions : undefined,
        startOnTick: false,
        endOnTick: false,
        labels: {
          format: "{value:%d/%m/%y}",
          rotation: isMobile ? -40 : 0,
          step: 1,
          overflow: "allow",
          crop: false,
          style: {
            color: theme.chartAxisColor,
            fontSize: isMobile ? "10px" : "14px",
            textTransform: "uppercase",
          },
        },
      },
      yAxis: {
        title: { text: null },
        visible: !isMobile,
        gridLineWidth: isMobile ? 0 : 1,
        gridLineColor: theme.chartGridColor,
        lineWidth: 0,
        ...yAxisBounds(series),
        startOnTick: true,
        endOnTick: true,
        labels: {
          style: { color: theme.chartAxisColor, fontSize: "14px" ,
            // x:-8,
          },

        },
      },
      plotOptions: {
        areaspline: {
          fillOpacity: 0.35,
          marker: { enabled: false },
        },
        series: {
          animation: { duration: 600 },
        },
      },
      tooltip: {
        shared: !isMobile,
        useHTML: isMobile,
        outside: isMobile,
        backgroundColor: theme.chartTooltipBg,
        borderColor: theme.chartTooltipBorder || theme.sectionBorder,
        borderRadius: isMobile ? 12 : 4,
        style: { color: theme.chartTooltipText },
        xDateFormat: "%d %b %Y",
        valueSuffix: isMobile ? "" : "%",
        formatter: isMobile
          ? function () {
              const point =
                this.points?.find((p) => p.series.name === "PORTFOLIO") ||
                this.point ||
                this.points?.[0];
              if (!point) return false;
              const date = new Date(point.x);
              const day = String(date.getUTCDate()).padStart(2, "0");
              const month = date
                .toLocaleString("en-US", { month: "short", timeZone: "UTC" })
                .toUpperCase();
              const year = String(date.getUTCFullYear()).slice(-2);
              const val = Number(point.y);
              const delta = Number.isNaN(val) ? null : val - 100;
              const valueLabel =
                delta == null
                  ? "—"
                  : `${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`;
              const labelColor = theme.mobileLabelColor;
              const valueColor = theme.chartTooltipText;
              return `
                <div style="padding:2px 0;min-width:90px;">
                  <div style="font-size:9px;font-weight:700;color:${labelColor};text-transform:uppercase;line-height:13.5px;margin-bottom:4px;">${day} ${month} ${year}</div>
                  <div style="font-size:12px;font-weight:700;color:${valueColor};letter-spacing:-0.24px;line-height:16px;">${valueLabel}</div>
                </div>
              `;
            }
          : undefined,
      },
      series,
    };
  }, [activeKeys, dataByKey, isMobile, isLight, theme]);

  const footerStats = useMemo(
    () => [
      {
        key: "portfolio",
        label: "PHC Portfolio",
        mobileLabel: "PORTFOLIO",
        value: resolveReturn(
          footerReturns.portfolio ?? footerReturns.phcPortfolio,
          portfolioData,
          FOOTER_FALLBACK.portfolio,
        ),
        color: "#45AC70",
        hasData: portfolioData?.length > 0,
      },
      {
        key: "nifty50",
        label: "NIFTY 50",
        mobileLabel: "NIFTY 50",
        value: resolveReturn(
          footerReturns.nifty50 ?? footerReturns.nifty50Return,
          benchmarkData,
          FOOTER_FALLBACK.nifty50,
        ),
        color: "#007EF0",
        hasData: benchmarkData?.length > 0,
      },
      {
        key: "nifty500",
        label: "NIFTY 500",
        mobileLabel: "NIFTY 500",
        value: resolveReturn(
          footerReturns.nifty500,
          dataByKey.nifty500,
          FOOTER_FALLBACK.nifty500,
        ),
        color: "#F6A823",
        hasData: (dataByKey.nifty500 || []).length > 0,
      },
      {
        key: "niftyMidcap",
        label: "NIFTY MIDCAP",
        mobileLabel: "MIDCAP",
        value: resolveReturn(
          footerReturns.niftyMidcap,
          dataByKey.niftyMidcap,
          FOOTER_FALLBACK.niftyMidcap,
        ),
        color: "#A846B9",
        hasData: (dataByKey.niftyMidcap || []).length > 0,
      },
      {
        key: "niftySmallcap",
        label: "NIFTY SMALLCAP",
        mobileLabel: "SMALLCAP",
        value: resolveReturn(
          footerReturns.niftySmallcap,
          dataByKey.niftySmallcap,
          FOOTER_FALLBACK.niftySmallcap,
        ),
        color: "#FF7043",
        hasData: (dataByKey.niftySmallcap || []).length > 0,
      },
    ],
    [portfolioData, benchmarkData, dataByKey, footerReturns],
  );

  const mobileMetrics = useMemo(() => {
    const items = footerStats
      .filter((s) => MOBILE_METRIC_STYLE[s.key])
      .slice(0, 4);
    const maxAbs = Math.max(
      1,
      ...items.map((s) => parseReturnNum(s.value)),
    );
    return items.map((s) => ({
      ...s,
      style: MOBILE_METRIC_STYLE[s.key],
      fillPct: (parseReturnNum(s.value) / maxAbs) * 100,
    }));
  }, [footerStats]);

  const mobileChips = BENCHMARKS.filter((b) =>
    ["nifty50", "nifty500", "niftyMidcap"].includes(b.key),
  );

  return (
    <>
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: "12px", md: theme.radiusSection },
        border: {
          xs: theme.mobileCardBorder,
          md: `1px solid ${theme.sectionBorder}`,
        },
        backgroundImage: { xs: "none", md: theme.sectionBg },
        backgroundColor: {
          xs: theme.mobileCardBg,
          md: theme.cardBg,
        },
        backdropFilter: { xs: "blur(6px)", md: "none" },
        boxShadow: { xs: "none", md: theme.cardShadow },
        px: { xs: "17px", md: "36px" },
        py: { xs: "17px", md: "23px" },
        overflow: { xs: "visible", md: "hidden" },
        "&::after": {
          content: { xs: "none", md: '""' },
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      {/* Title */}
      <Text
        sx={{
          position: "relative",
          zIndex: 1,
          fontWeight: { xs: 600, md: 700 },
          fontSize: { xs: 14, md: 24 },
          color: { xs: theme.mobileLabelColor, md: theme.textPrimary },
          letterSpacing: { xs: "1.4px", md: "-0.72px" },
          textTransform: { xs: "uppercase", md: "none" },
          mb: { xs: 2, md: "18px" },
          whiteSpace: "nowrap",
          lineHeight: { xs: "16px", md: "normal" },
        }}
      >
        Performance Chart
      </Text>

      {/* —— MOBILE: ADD BENCHMARK chips —— */}
      <Box sx={{ display: { xs: "block", md: "none" }, mb: 1, zIndex: 1, position: "relative" }}>
        <Text
          sx={{
            fontSize: 10,
            color: theme.mobileLabelColor,
            textTransform: "uppercase",
            lineHeight: "15px",
            mb: "12px",
          }}
        >
          ADD BENCHMARK:
        </Text>
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            pb: 0.5,
            mx: "-4px",
            px: "4px",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {mobileChips.map((b) => (
            <MobileChip
              key={b.key}
              benchmark={b}
              active={activeKeys.includes(b.key)}
              onClick={() => toggleBenchmark(b.key)}
              theme={theme}
            />
          ))}
        </Box>
      </Box>

      {/* —— DESKTOP: ADD BENCHMARK bar —— */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          zIndex: 1,
          borderRadius: "20px",
          border: "1px solid rgba(166,166,166,0.18)",
          backgroundImage: theme.sectionBg,
          backgroundColor: theme.cardBg,
          boxShadow: theme.cardShadow,
          px: "21px",
          py: "20px",
          mb: "18px",
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            minWidth: "max-content",
          }}
        >
          <Text
            sx={{
              fontWeight: 600,
              fontSize: 20,
              color: theme.textPrimary,
              whiteSpace: "nowrap",
              flexShrink: 0,
              lineHeight: "normal",
            }}
          >
            ADD BENCHMARK:
          </Text>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexShrink: 0,
            }}
          >
            {BENCHMARKS.map((b) => (
              <DesktopChip
                key={b.key}
                benchmark={b}
                active={activeKeys.includes(b.key)}
                onClick={() => toggleBenchmark(b.key)}
              />
            ))}
            <Box
              onClick={handleToggleShowAll}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: "12px",
                py: "10px",
                borderRadius: "12px",
                cursor: "pointer",
                flexShrink: 0,
                whiteSpace: "nowrap",
                bgcolor: showAll
                  ? "rgba(0,126,240,0.35)"
                  : "rgba(0,126,240,0.2)",
                border: showAll
                  ? "1px solid #007EF0"
                  : "1px solid rgba(0,126,240,0.55)",
              }}
            >
              <Text
                sx={{
                  fontWeight: 500,
                  fontSize: 16,
                  color: "#007EF0",
                  whiteSpace: "nowrap",
                  lineHeight: "normal",
                }}
              >
                {showAll ? "Hide" : "Show all"}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Chart area */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          borderRadius: { xs: 0, md: "20px" },
          border: {
            xs: "none",
            md: `1px solid ${theme.sectionBorder}`,
          },
          backgroundImage: { xs: "none", md: theme.sectionBg },
          backgroundColor: { xs: "transparent", md: theme.cardBg },
          boxShadow: { xs: "none", md: theme.cardShadow },
          overflow: { xs: "visible", md: "hidden" },
          mb: { xs: 0, md: "18px" },
          "&::after": {
            content: { xs: "none", md: '""' },
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            pointerEvents: "none",
            boxShadow: theme.insetGlow,
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: { xs: 248, md: 321 },
            }}
          >
            <CircularProgress sx={{ color: "#007EF0" }} />
          </Box>
        ) : hasChartData && chartOptions.series?.length > 0 ? (
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Chart options={chartOptions} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              minHeight: { xs: 248, md: 321 },
              alignItems: "center",
              justifyContent: "center",
              color: theme.textMuted,
              fontSize: 16,
            }}
          >
            No data found
          </Box>
        )}
      </Box>

      {/* —— DESKTOP: footer returns row —— */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            height: "1px",
            width: "100%",
            bgcolor: theme.sectionBorder,
            mb: "24px",
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: "31px",
            overflowX: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              flexShrink: 0,
            }}
          >
            <Text
              sx={{
                fontSize: 16,
                color: "#789094",
                whiteSpace: "nowrap",
                lineHeight: "normal",
              }}
            >
              {footerStats[0].label}
            </Text>
            <Text
              sx={{
                fontWeight: 700,
                fontSize: 24,
                color: footerStats[0].color,
                lineHeight: "normal",
              }}
            >
              {footerStats[0].value}
            </Text>
          </Box>
          <Box
            sx={{
              width: "1px",
              height: "94px",
              bgcolor: theme.dividerColor,
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "62px",
              flexShrink: 0,
            }}
          >
            {footerStats.slice(1).map((stat) => {
              const dimmed = !showAll && !activeKeys.includes(stat.key);
              return (
                <Box
                  key={stat.key}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    opacity: dimmed ? 0.5 : 1,
                    flexShrink: 0,
                  }}
                >
                  <Text
                    sx={{
                      fontSize: 16,
                      color: "#789094",
                      whiteSpace: "nowrap",
                      lineHeight: "normal",
                    }}
                  >
                    {stat.label}
                  </Text>
                  <Text
                    sx={{
                      fontWeight: 700,
                      fontSize: 24,
                      color: stat.color,
                      lineHeight: "normal",
                    }}
                  >
                    {stat.value}
                  </Text>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>

    {/* —— MOBILE: 2×2 metric grid below chart  —— */}
    <Box
      sx={{
        display: { xs: "block", md: "none" },
        width: "100%",
        mt: 2,
      }}
    >
      <Grid container spacing={2}>
        {mobileMetrics.map((stat) => (
          <Grid item xs={6} key={stat.key}>
            <MobileMetricCard
              label={stat.mobileLabel}
              value={stat.value}
              style={stat.style}
              fillPct={stat.fillPct}
              theme={theme}
            />
          </Grid>
        ))}
      </Grid>
    </Box>

    <Box sx={{ mt: 2.5 }}>
      <Text
        sx={{
          fontWeight: 700,
          fontSize: { xs: 14, md: 16 },
          color: theme.textPrimary,
          lineHeight: 1.4,
          mb: 0.75,
        }}
      >
        Performance Disclaimer:
      </Text>
      <Text
        sx={{
          fontSize: { xs: 13, md: 14 },
          lineHeight: 1.6,
          color: theme.mobileLabelColor,
          fontWeight: 400,
        }}
      >
        The portfolio performance shown above represents an indicative one-year
        performance of your existing portfolio compared with the selected
        benchmark. Since portfolio holdings may have been purchased or sold
        through multiple transactions on different dates, the average buy/sell
        price displayed for a security may not correspond to any single
        transaction date shown in the performance chart. Accordingly, the chart
        is intended to provide a consolidated historical view of portfolio
        performance and should not be interpreted as a transaction-level
        performance calculation or as reflecting the exact return attributable
        to each individual purchase or sale.
      </Text>
    </Box>
    </>
  );
};

export default PerformanceChart;
